/* ═══════════════════════════════════════════════════════════════════
   LibraryTab — Royal Shelf 3D bookshelf (Library section of Gallery).
   Extracted from BlissGallery so each tab lives in its own module.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MoreVertical, Download, Share2 } from 'lucide-react';
import { PILLAR_CHAPTERS } from '../BlissBook';

const GOLD = '#D4AF37';
const GOLD_LIGHT = '#F5D97A';

const extractSpineColor = (gradStr) => {
  const m = /#([0-9a-fA-F]{6})/g.exec(gradStr);
  return m ? `#${m[1]}` : '#5a2a10';
};

export const LIBRARY_BOOKS = [
  { mode: 'quote',  title: 'Royal Quote',     subtitle: 'A Royal Decree',   spine: '#6e1620', cover: 'radial-gradient(ellipse at 50% 38%, #6e1620 0%, #4a0a14 45%, #2a040a 100%)' },
    { mode: 'script', title: 'Empire Journal',  subtitle: 'A Sealed Entry',   spine: '#173a7a', cover: 'radial-gradient(ellipse at 50% 38%, #173a7a 0%, #0a1f4a 50%, #050b1f 100%)' },
  { mode: 'ledger', title: 'Imperial Ledger', subtitle: 'A Soul Inscribed', spine: '#5a2a10', cover: 'radial-gradient(ellipse at 50% 38%, #5a2a10 0%, #3a1808 50%, #1a0805 100%)' },
  ...PILLAR_CHAPTERS.map((p) => ({
    mode: `pillar-${p.id}`,
    title: p.label,
    subtitle: `Pillar of ${p.label}`,
    spine: extractSpineColor(p.cover),
    cover: p.cover,
  })),
];

const BookCard = ({ book, empireName, profilePicture, onOpen, onAction }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const initial = (empireName || 'A')[0].toUpperCase();
  return (
    <div className="relative" data-testid={`shelf-book-${book.mode}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
        className="absolute z-30 w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
        style={{ top: 6, right: 6, background: 'rgba(0,0,0,0.55)', border: `1px solid ${GOLD}88`, color: GOLD_LIGHT, backdropFilter: 'blur(6px)' }}
        data-testid={`shelf-book-${book.mode}-menu`} aria-label="More actions">
        <MoreVertical size={14} />
      </button>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-[35]" onClick={() => setMenuOpen(false)} />
          <div className="absolute z-[40] rounded-lg overflow-hidden"
            style={{
              top: 42, right: 6, minWidth: 148,
              background: 'rgba(20,12,4,0.96)', border: `1px solid ${GOLD}66`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.65), 0 0 0 1px rgba(212,175,55,0.15)',
              backdropFilter: 'blur(10px)',
            }}>
            <button onClick={() => { setMenuOpen(false); onAction('download', book); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 active:bg-white/5"
              style={{ color: GOLD_LIGHT }}
              data-testid={`shelf-book-${book.mode}-download`}>
              <Download size={14} />
              <span className="font-['Outfit'] text-[12px] tracking-wide">Download PDF</span>
            </button>
            <div style={{ height: 1, background: `${GOLD}33` }} />
            <button onClick={() => { setMenuOpen(false); onAction('share', book); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 active:bg-white/5"
              style={{ color: GOLD_LIGHT }}
              data-testid={`shelf-book-${book.mode}-share`}>
              <Share2 size={14} />
              <span className="font-['Outfit'] text-[12px] tracking-wide">Share Link</span>
            </button>
          </div>
        </>
      )}

      <motion.button whileTap={{ scale: 0.96 }} whileHover={{ y: -3 }} onClick={onOpen}
        className="relative w-full block"
        style={{ aspectRatio: '0.72 / 1', paddingRight: 7 }}
        data-testid={`shelf-book-${book.mode}-open`}>
        <div className="relative w-full h-full">
          <div className="absolute" style={{
            right: -7, top: 4, bottom: 4, width: 7,
            background: 'repeating-linear-gradient(180deg, #f0dfa8 0px, #f0dfa8 1px, #d8c084 1px, #d8c084 2px)',
            borderRadius: '0 4px 4px 0',
            boxShadow: 'inset -1px 0 2px rgba(0,0,0,0.45), 0 4px 8px rgba(0,0,0,0.35)',
          }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center overflow-hidden"
            style={{
              background: book.cover, borderRadius: '5px 3px 3px 5px',
              boxShadow:
                'inset 0 0 60px rgba(0,0,0,0.55), inset 0 0 14px rgba(0,0,0,0.6),' +
                '0 12px 24px rgba(0,0,0,0.55), 0 4px 8px rgba(0,0,0,0.4)',
            }}>
            <div aria-hidden className="absolute pointer-events-none" style={{
              left: 0, top: 0, bottom: 0, width: 8,
              background: 'linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)',
              borderRadius: '5px 0 0 5px',
            }} />
            <div aria-hidden className="absolute inset-1.5 pointer-events-none"
              style={{ border: `1.5px solid ${GOLD}`, borderRadius: 4, boxShadow: 'inset 0 0 0 1px rgba(255,241,184,0.35)' }} />
            <div aria-hidden className="absolute inset-2.5 pointer-events-none"
              style={{ border: '0.5px solid rgba(212,175,55,0.5)', borderRadius: 3 }} />

            <div className="relative" style={{ width: 42, height: 42, marginBottom: 8 }}>
              <div className="absolute inset-0 rounded-full overflow-hidden"
                style={{
                  border: `1.5px solid ${GOLD}`, boxShadow: '0 0 8px rgba(212,175,55,0.45)',
                  background: profilePicture ? '#1a0608' : 'radial-gradient(circle at 50% 32%, #f6e6b6, #c69b3f 70%, #6b4612 100%)',
                }}>
                {profilePicture ? (
                  <img src={profilePicture} alt={empireName} draggable={false}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700, fontStyle: 'italic', fontSize: 22, color: '#3a2410' }}>
                    {initial}
                  </div>
                )}
              </div>
            </div>

            <h3 className="font-['Cormorant_Garamond'] font-bold relative z-10 px-2 leading-tight"
              style={{
                fontSize: 'clamp(15px, 5.2vw, 19px)',
                background: 'linear-gradient(180deg, #FFF1B8 0%, #F5D97A 35%, #C69B3F 75%, #7A5418 100%)',
                WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
                textShadow: '0 1px 0 rgba(0,0,0,0.4)', letterSpacing: '0.03em',
              }}>
              {book.title}
            </h3>
            <div className="my-1.5 w-12 h-[1px]"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD_LIGHT}, transparent)` }} />
            <p className="font-['Cormorant_Garamond'] italic relative z-10 px-2"
              style={{ color: '#F5E6A0', fontSize: 'clamp(9px, 3vw, 11px)', letterSpacing: '0.06em' }}>
              {book.subtitle}
            </p>
          </div>
        </div>
      </motion.button>
    </div>
  );
};

export const LibraryTab = ({ empireName, profilePicture, onOpenBook, onAction }) => (
  <div className="flex-1 overflow-y-auto book-no-print"
    style={{ background: 'repeating-linear-gradient(180deg, rgba(82,46,8,0.10) 0px, rgba(82,46,8,0.10) 1px, transparent 1px, transparent 6px)' }}>
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-2 gap-4 px-4 pt-5 pb-8"
      data-testid="bliss-gallery-shelf">
      {LIBRARY_BOOKS.map((b) => (
        <BookCard
          key={b.mode} book={b}
          empireName={empireName} profilePicture={profilePicture}
          onOpen={() => onOpenBook(b.mode)} onAction={onAction}
        />
      ))}
    </motion.div>
    <p className="text-center font-['Cormorant_Garamond'] italic text-white/30 pb-6 px-6">
      — tap any volume to read it, the three-dots to download or share —
    </p>
  </div>
);
